/* eslint-disable max-lines */
import { NgTemplateOutlet } from '@angular/common';
import {
  ChangeDetectionStrategy,
  ChangeDetectorRef,
  Component,
  computed,
  DestroyRef,
  effect,
  ElementRef,
  inject,
  Input,
  input,
  OnChanges,
  OnDestroy,
  output,
  Renderer2,
  signal,
  SimpleChanges,
  untracked,
  ViewChild,
  ViewContainerRef,
} from '@angular/core';
import { takeUntilDestroyed } from '@angular/core/rxjs-interop';

import { Subject } from 'rxjs';
import { take, takeUntil } from 'rxjs/operators';

import type { UserBase } from '@core/account-types';
import type { Attachment } from '@core/attachments-types';
import { AttachmentItemComponent } from '@core/attachments-ui';
import { filterBoolean } from '@core/common-legacy';
import { I18nService, TranslatePipe } from '@core/i18n-legacy';
import type {
  BaseNote,
  NoteDeleteAttachmentEvent,
  NoteDiscardEvent,
  NoteEditEvent,
  NoteLocalStorageKeyBuilder,
  NoteReplyChangesEvent,
  NoteReplyClickEvent,
  NoteReplyTypingStartEvent,
  NoteSave,
  NoteUploadAttachmentEvent,
  SavedNoteId,
} from '@core/notes-types';
import type { ConfigEvents, MentionElement } from '@core/prosemirror-legacy';
import { ChecklistPluginChecklistChange } from '@core/prosemirror-legacy';
import { RenderDirective, WysiwygContext$ } from '@core/prosemirror-legacy/angular';
import {
  ProsemirrorDefaultConfig,
  ProsemirrorDefaultConfigDirective,
} from '@core/prosemirror-legacy/webapp';
import { NoteReactionsContainerComponent } from '@core/reactions-legacy';
import { DateComponent } from '@core/time-ui';
import {
  BadgeComponent,
  ButtonComponent,
  ConnectedOverlayDirective,
  DialogService,
  DividerComponent,
  DropdownComponent,
  DropdownItemComponent,
  DropdownWidth,
  GroupComponent,
  IconButtonComponent,
  IconComponent,
  OverlayOriginDirective,
  RtTooltip,
  ThreadExpanderComponent,
} from '@core/user-interface-legacy';
import { generateUuid } from '@core/utils-legacy';
import { VisibilitySelectorContainerComponent } from '@core/visibility-legacy';
import { VisibilityLevel } from '@core/visibility-types';

import { injectNotesTranslations } from '../helpers';
import { NoteAvatarComponent } from '../note-avatar/note-avatar.component';
import {
  NOTE_AVATAR_UPDATE_STRATEGY,
  type NoteAvatarAuthor,
} from '../note-avatar/note-avatar.types';
import { RtNoteAvatarUpdate } from '../note-avatar/strategy/rt-note-avatar-update';
import { NoteFormComponent, type VisibilitySelector } from '../note-form/note-form.component';
import { NoteTypingIndicatorMessageComponent } from '../note-typing-indicator-message/note-typing-indicator-message.component';

type ReplyNote = {
  id?: number | string;
  text?: string;
};

@Component({
  selector: 'rt-note-item',
  templateUrl: './note-item.component.html',
  styleUrls: ['./note-item.component.less'],
  changeDetection: ChangeDetectionStrategy.OnPush,
  host: {
    '[class.context-private]': 'isPrivate() && !noteIsReply()',
  },
  providers: [
    { provide: WysiwygContext$, useFactory: () => new WysiwygContext$(null) },
    { provide: NOTE_AVATAR_UPDATE_STRATEGY, useClass: RtNoteAvatarUpdate },
  ],
  imports: [
    DividerComponent,
    NoteAvatarComponent,
    GroupComponent,
    IconButtonComponent,
    OverlayOriginDirective,
    RtTooltip,
    ConnectedOverlayDirective,
    DropdownComponent,
    DropdownItemComponent,
    IconComponent,
    DateComponent,
    AttachmentItemComponent,
    ButtonComponent,
    NoteFormComponent,
    NoteTypingIndicatorMessageComponent,
    ThreadExpanderComponent,
    TranslatePipe,
    NgTemplateOutlet,
    VisibilitySelectorContainerComponent,
    RenderDirective,
    ProsemirrorDefaultConfigDirective,
    BadgeComponent,
    NoteReactionsContainerComponent,
  ],
})
export class NoteItemComponent<Note extends BaseNote<Note>> implements OnChanges, OnDestroy {
  private readonly i18n = inject(I18nService);
  private readonly dialogService = inject(DialogService);
  private readonly viewRef = inject(ViewContainerRef);
  private readonly renderer = inject(Renderer2);
  private readonly elRef = inject(ElementRef);
  private readonly wysiwygContext$ = inject(WysiwygContext$);
  private readonly destroyRef = inject(DestroyRef);
  private readonly cdRef = inject(ChangeDetectorRef);

  protected readonly notesTranslations = injectNotesTranslations();

  public note = input.required<Note>();
  public editing = input<boolean>(false);
  public replying = input<boolean | null>(null);
  public canReply = input(true);
  public canPin = input(true);
  public hasFileUpload = input(true);
  public hasReactions = input(true);
  public canHighLevelDataAccess = input(false);
  public noteIsReply = input(false);

  @Input() public privateContext: boolean = false;
  @Input() public cachedReply: NoteReplyChangesEvent;
  @Input() public canDeleteAttachments: boolean = false;
  @Input()
  public currentlyTypedNotes: Array<{
    admin: UserBase;
    replyToId?: number | null;
  }> = [];

  @Input() public noteSaveLoading: Array<string | number> = [];
  @Input() public notesDeleteLoading: Array<string | number> = [];
  @Input() public noteAttachmentLoading: { [key: string]: string[] };
  @Input() public notesBeingEdited: Array<string | number> = [];
  @Input() public notesBeingRepliedTo: Array<string | number> = [];
  @Input() public allNotesFlat: Note[];

  @Input() public attachmentsTemp: Record<string, Array<{ uuid: string; attachment: Attachment }>>;

  @Input() public isReadOnly = false;

  @Input() public hasConnector = false;
  @Input() public moreButtonVisible = true;

  public isOwnerFn = input.required<(note: Note) => boolean>();
  public getOwnerFn = input.required<(note: Note) => NoteAvatarAuthor | null>();
  public localStorageKeyBuilder = input.required<NoteLocalStorageKeyBuilder<Note>>();
  public noteToMention = input.required<(note: Note) => MentionElement | null>();
  public isAutomatedActionNoteFn = input.required<(note: Note) => boolean>();
  public visibilitySelectorComponent = input<VisibilitySelector>();
  public visibilityComponentInputs = input<Partial<InstanceType<VisibilitySelector>>>();

  public replyTypingStart = output<NoteReplyTypingStartEvent>();
  public replyTypingFinish = output<void>();

  public noteDiscardClick = output<NoteDiscardEvent>();
  public noteEditClick = output<NoteEditEvent>();
  public noteReplyClick = output<NoteReplyClickEvent>();
  public noteSaveClick = output<NoteSave<Note>>();
  public noteChecklistClick = output<{
    id: number;
    bodyJson: object;
  }>();
  public noteDeleteClick = output<SavedNoteId>();
  public notePinClick = output<SavedNoteId>();
  public noteUnpinClick = output<SavedNoteId>();
  public replyChanges = output<NoteReplyChangesEvent>();
  public uploadAttachmentClick = output<NoteUploadAttachmentEvent>();
  public deleteAttachmentClick = output<NoteDeleteAttachmentEvent<Note>>();

  public author = computed(() => this.getOwnerFn()(this.note()));
  public isAutomatedActionNote = computed(() => this.isAutomatedActionNoteFn()(this.note()));
  public authorName = computed(() => this.author()?.name);
  public replyNote = signal<ReplyNote | null>(null);
  public replyTempId = signal<number | string>(generateUuid());
  public isEditable = computed(() => this.isOwnerFn()(this.note()));
  public isPrivate = computed(() => this.note().visibility?.level !== VisibilityLevel.Public);

  public isDeletable = computed(() => this.canHighLevelDataAccess() || this.isEditable());

  public rendererChanged$ = new Subject<void>();

  public VisibilityLevel: typeof VisibilityLevel = VisibilityLevel;
  public ProsemirrorConfig = ProsemirrorDefaultConfig;
  public DropdownWidth = DropdownWidth;

  public noteItemIsHovered: boolean = false;
  public dropdownIsOpened: boolean = false;
  public allRepliesVisible = false;

  protected dropdownOptionsVisible = {
    pin: computed(() => !this.noteIsReply() && !this.note().pinnedAt && this.canPin()),
    edit: computed(() => this.isEditable() && this.note().pinnedAt),
    delete: this.isDeletable,
  };
  protected anyDropdownOptionsVisible = computed(() =>
    Object.values(this.dropdownOptionsVisible).some(v => v()),
  );

  // eslint-disable-next-line @typescript-eslint/member-ordering
  public _noteRenderer: RenderDirective<InstanceType<NoteItemComponent<Note>['ProsemirrorConfig']>>;
  @ViewChild('noteRenderer', { static: false })
  private set noteRenderer(
    value: RenderDirective<InstanceType<NoteItemComponent<Note>['ProsemirrorConfig']>>,
  ) {
    if (value === this._noteRenderer) return;

    this._noteRenderer = value;
    this.rendererChanged$.next();
    this.setCheckboxEdit(value);
  }

  private setWysiwygContext = effect(() => {
    const authorName = this.authorName();
    if (!authorName) return;

    untracked(() =>
      this.wysiwygContext$.next({
        owner: { name: authorName },
      }),
    );
  });

  public ngOnChanges(changes: SimpleChanges): void {
    if (changes.replying?.currentValue && this.replyTempId()) {
      this.replyNote.set({ id: this.replyTempId() });
    }
  }

  public ngOnDestroy(): void {
    if (this.replying()) {
      this.replyTypingFinish.emit();
    }
    this.rendererChanged$.next();
    this.rendererChanged$.complete();
  }

  public get typingAdmins(): UserBase[] {
    return this.currentlyTypedNotes
      .filter(note => note.replyToId === this.note().id)
      .map(note => note.admin);
  }

  public get showHeaderButtons(): boolean {
    if (this.noteItemIsHovered || this.dropdownIsOpened) {
      if (
        this.isEditable() ||
        this.isDeletable() ||
        (!this.note().pinnedAt && !this.noteIsReply())
      ) {
        return true;
      }
      if (this.isEditable() && this.isDeletable() && this.note().pinnedAt) {
        return true;
      }
    }
    return false;
  }

  public showReply(): void {
    this.noteReplyClick.emit({ id: <number>this.note().id! });
    this.replyTypingStart.emit({ replyToId: this.note().id! });
  }

  public showEdit(): void {
    this.noteEditClick.emit({ id: this.note().id! });
    this.replyTypingFinish.emit();
  }

  public cancel(): void {
    this.noteDiscardClick.emit({ id: this.note().id! });
    this.replyTypingFinish.emit();
    this.changeNoteVisibilityClass(this.isPrivate());
  }

  public changeNoteVisibilityClass(isPrivate: boolean): void {
    isPrivate
      ? this.renderer.addClass(this.elRef.nativeElement, 'context-private')
      : this.renderer.removeClass(this.elRef.nativeElement, 'context-private');
  }

  public toggleDropdown(dropdown: ConnectedOverlayDirective): void {
    this.dropdownIsOpened = !dropdown.open;
    dropdown.open = !dropdown.open;
    this.cdRef.detectChanges();
  }

  public deleteNote(): void {
    this.dialogService
      .error(
        {
          title: this.i18n.translate(this.notesTranslations.deleteTitle),
          message: this.i18n.translate(this.notesTranslations.deleteMessage),
          agreeLabel: this.i18n.translate(this.notesTranslations.deleteAgreeLabel),
        },
        this.viewRef,
      )
      .pipe(take(1), filterBoolean)
      .subscribe(() => {
        this.noteDeleteClick.emit(<number>this.note().id!);
      });
  }

  public pinNote(): void {
    this.notePinClick.emit(<number>this.note().id!);
  }

  public unpinNote(): void {
    this.noteUnpinClick.emit(<number>this.note().id!);
  }

  public setHeaderButtonsVisibility(visible: boolean): void {
    this.noteItemIsHovered = visible;
    this.cdRef.detectChanges();
  }

  public showAllReplies(): void {
    this.allRepliesVisible = true;
    this.cdRef.detectChanges();
  }

  public onWysiwygEvent(
    event: ConfigEvents<InstanceType<NoteItemComponent<Note>['ProsemirrorConfig']>>,
  ) {
    if (event instanceof ChecklistPluginChecklistChange) {
      const { doc } = event.state;
      const { id } = this.note();
      this.noteChecklistClick.emit({ id: <number>id, bodyJson: { doc } });
    }
  }

  private setCheckboxEdit(
    noteRenderer: RenderDirective<InstanceType<NoteItemComponent<Note>['ProsemirrorConfig']>>,
  ) {
    if (!noteRenderer) return;

    noteRenderer.publicApi$
      .pipe(filterBoolean, takeUntil(this.rendererChanged$), takeUntilDestroyed(this.destroyRef))
      .subscribe(({ checklistService, mentionsService }) => {
        const isOwner = this.isOwnerFn()(this.note());
        checklistService.setCanEdit(isOwner || this.isAutomatedActionNote());
      });
  }
}
